<template>
  <div>
    <h1>Create your public profile</h1>

    <p class="mt-3 font-sans-book text-lg" style="line-height: 1.75rem">
      On your dashboard, you’ll be able to add a photo, contact information, and share your story. All of your events and resources will be displayed here so community members can easily learn about you and your offerings.
    </p>

    <div class="spacer" />

    <div style="margin-bottom: -1.5rem">
      <label>
        Do you want to be represented as yourself or your organization? <span class="text-red">*</span>
      </label>
      <div class="group__radio">
        <div class="flex flex-wrap select-box">
          <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
            <input type="radio" value="individual" v-model="form.organization_type" v-validate="'required'" name="organization_type" />
            <span class="inline-block mr-2 radio" /> Myself
          </label>
          <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
            <input type="radio" value="organization" v-model="form.organization_type" v-validate="'required'" name="organization_type" />
            <span class="inline-block mr-2 radio" /> My Organization
          </label>
        </div>
      </div>
      <div v-show="errors.has('organization_type')" class="error-message">
        {{ (errors.first('organization_type') || '').replace('_', ' ') }}
      </div>
    </div>

    <div class="spacer" />

    <div>
      <label>
        What country {{ form.organization_type === 'individual' ? 'are you' : 'is your organization' }} located in?
        <span class="text-red">*</span>
      </label>
      <select v-model="form.country" v-validate="'required'" name="country">
        <option :value="null" disabled> -- please select -- </option>
        <option v-for="country in options.countries" :key="country.code" :value="country.code">
          {{ country.name }}
        </option>
      </select>
      <div v-show="errors.has('country')" class="error-message">
        {{ errors.first('country') }}
      </div>
    </div>

    <div class="spacer" />

    <div class="flex flex-col md:flex-row">
      <div :class="[isFromUnitedStates ? 'w-full md:w-1/2' : 'w-full']">
        <label>
          City <span class="text-red">*</span>
        </label>
        <input v-validate="'required'" name="city" v-model="form.city" type="text" />
        <div v-show="errors.has('city')" class="error-message">
          {{ errors.first('city') }}
        </div>
      </div>

      <div class="spacer block md:hidden" />

      <template v-if="isFromUnitedStates">
        <div class="w-full md:w-1/4 pl-0 md:pl-2">
          <label>
            State <span class="text-red">*</span>
          </label>
          <select v-validate="'required'" name="state" v-model="form.state">
            <option :value="null">-- select --</option>
            <option v-for="state in options.states" :key="state.code">
              {{ state.name }}
            </option>
          </select>
          <div v-show="errors.has('state')" class="error-message">
            {{ errors.first('state') }}
          </div>
        </div>

        <div class="spacer block md:hidden" />

        <div class="w-full md:w-1/4 pl-0 md:pl-2">
          <label>
            Zip code <span class="text-red">*</span>
          </label>
          <input v-validate="'required'" name="zip_code" v-model="form.zip_code" type="text" />
          <div v-show="errors.has('zip_code')" class="error-message">
            {{ (errors.first('zip_code') || '').replace('_', ' ') }}
          </div>
        </div>
      </template>
    </div>

    <div class="spacer" />

    <div class="w-full">
      <label>
        {{ isIndividual ? 'Public' : 'Organization' }} name <span class="text-red">*</span>
      </label>
      <input v-validate="'required|min:5'" name="name" type="text" class="w-full" v-model="form.name" />
      <div v-show="errors.has('name')" class="error-message">
        {{ errors.first('name') }}
      </div>
    </div>

    <template v-if="!isIndividual">
      <div class="spacer" />
      <div>
        <label>
          Organization type
          <span class="text-red">*</span>
        </label>
        <select v-model="form.organization_subtype" v-validate="'required'" name="organization_subtype">
          <option :value="null" disabled> -- please select -- </option>
          <option v-for="subtype in options.organizationSubtypes" :key="subtype" :value="subtype">
            {{ subtype }}
          </option>
        </select>
        <div v-show="errors.has('organization_subtype')" class="error-message">
          {{ (errors.first('organization_subtype') || '').replace('_', ' ') }}
        </div>
      </div>
    </template>

    <div class="spacer" />

    <div class="w-full">
      <label>
        Phone number
        <span class="block text-sm font-sans-book text-n-gray-dark mb-5">
          Optional. This is not displayed on your profile and is used only if we need to contact you
        </span>
      </label>
      <input type="text" class="w-full" v-model="form.phone_number" />
    </div>

    <div class="spacer" />

    <div class="w-full">
      <label>
        Website
      </label>
      <UrlInput v-model="form.website" />
      <div v-show="errors.has('website')" class="error-message">
        {{ errors.first('website') }}
      </div>
    </div>

    <div class="spacer" />

    <hr class="w-px bg-n-gray border border-n-gray w-full" />

    <div class="spacer" />

    <div class="flex justify-center">
      <button @click="createProfile" class="button">create my profile</button>
    </div>

    <div class="spacer" />
  </div>
</template>

<script>
import { geocodeAPI } from "../../../api/geocodeAPI";
import { optionsAPI } from "../../../api/optionsAPI";
import {authAPI} from "../../../api/authAPI";
import UrlInput from "../../global/UrlInput.vue";
import {analyticsDataLayer} from "../../../api/analyticsDataLayer";

export default {
  name: "CreateProfile",
  components: {UrlInput},
  data() {
    return {
      options: {
        countries: geocodeAPI.getCountries(),
        states: geocodeAPI.getStates(),
        organizationSubtypes: optionsAPI.getOrganizationSubtypes(),
      },
      form: {
        organization_type: '',
        name: '',
        country: 'USA',
        city: '',
        state: null,
        zip_code: '',
        organization_subtype: null,
        phone_number: '',
        website: ''
      }
    }
  },
  computed: {
    isFromUnitedStates() {
      return this.form.country === 'USA';
    },
    isIndividual() {
      return this.form.organization_type === 'individual';
    }
  },
  methods: {
    async createProfile() {
      const hasNoFormErrors = await this.$validator.validateAll();

      if (!hasNoFormErrors) {
        // this.gotoFirstValidationError();
        return;
      }

      const response = await authAPI.register.createProfile(this.form);

      if (response.success) {
        await this.$store.dispatch("getMe");
        analyticsDataLayer.emit('register_profile_created');
        this.$router.push({ name: 'register-jedi-pledge' });

        return;
      }

      Object.keys(response.errors).forEach(formAttr => {
        const errorMessage = response.errors[formAttr].find(() => true);
        this.errors.add({ field: formAttr, msg: errorMessage });
      });
    }
  }
}
</script>
